/**
 * Alat porositas efektif — metodenya, dan titik serpih yang dibaca metode itu.
 *
 * Sebangun dengan `VshTool` dan `PhitTool`, dan dipakai oleh dua layar yang
 * sama: rel parameter di ruang kerja dan tahap ketiga di rel alur deterministik.
 *
 * Yang membedakannya dari kedua alat sebelumnya adalah bahwa pilihannya di sini
 * memutuskan kurva mana yang dibaca sama sekali. Satu keluarga mengurangi
 * bagian serpih dari PHIT tahap dua; yang lain tidak menyentuh PHIT dan
 * menghitung ulang dari kurva alatnya sendiri dengan suku serpih di dalam
 * persamaannya — sebagaimana modul porositas Geolog. Keduanya boleh berbeda
 * kurva dengan tahap dua, dan itulah sebabnya mesin menjepit hasilnya di PHIT.
 */
import { useState } from "react";

import type { ParameterValues } from "../types";
import {
  CurveSourceFields,
  channelsRead,
  sameParam,
  type AvailableCurve,
} from "./CurveSource";
import { Button, Dropdown, Modal, NumberField, ParamCaption, Toggle } from "./ui";

/**
 * Cara porositas efektif dihitung.
 *
 * Urutannya urutan pilihan, bukan abjad: pengurangan dari PHIT lebih dulu sebab
 * ia satu-satunya yang tidak menuntut kurva sendiri dan karena itu selalu bisa
 * dihitung, dan keempat sisanya menyusul dalam urutan yang sama dengan menu
 * porositas total supaya kedua tahap dibaca sebagai satu pasang.
 */
export const PHIE_METHODS = [
  { value: "total_shale", label: "Total − Vsh·PHIsh", hint: "from PHIT" },
  { value: "density_neutron_shale", label: "Density–neutron − shale", hint: "RHOB + NPHI" },
  { value: "density_shale", label: "Density − shale", hint: "RHOB" },
  { value: "neutron_shale", label: "Neutron − shale", hint: "NPHI" },
  { value: "sonic_shale", label: "Sonic − shale", hint: "DTC" },
];

/**
 * Rumus tiap pilihan, ditulis sebagaimana ia dihitung di mesin.
 *
 * Kuncinya metode, kecuali sonik yang bergantung pada lengkungnya dan
 * densitas-neutron yang bergantung pada rata-ratanya; lihat `phieFormula`.
 */
export const PHIE_FORMULAS: Record<string, string> = {
  total_shale: "PHIE = PHIT − Vsh · PHIsh",
  density_shale: "PHIE = PHID − Vsh · PHIDsh,  PHIDsh = (RHOma − RHOsh)/(RHOma − RHOfl)",
  neutron_shale: "PHIE = NPHI − Vsh · NPHIsh",
  wyllie: "PHIE = PHIS − Vsh · PHISsh,  PHIS = (DT − DTma)/(DTfl − DTma)/Bcp",
  raymer_hunt: "PHIE = PHIS − Vsh · PHISsh,  PHIS = 0.625 (1 − DTma/DT)",
  rms: "PHIE = √((PHIDe² + PHINe²) / 2) across gas crossover",
  mean: "PHIE = (PHIDe + PHINe) / 2",
};

/** Rumus yang berlaku bagi sebuah setelan penuh, bukan bagi metodenya saja. */
export const phieFormula = (params: ParameterValues): string => {
  const method = String(params.phie_method ?? "total_shale");
  if (method === "sonic_shale") {
    return PHIE_FORMULAS[String(params.phit_sonic_method ?? "wyllie")] ?? "";
  }
  if (method === "density_neutron_shale") {
    return PHIE_FORMULAS[params.phit_gas_corrected === false ? "mean" : "rms"];
  }
  return PHIE_FORMULAS[method] ?? "";
};

/**
 * Keterangan bagi pilihan yang membawa akibat di luar kotaknya sendiri.
 *
 * Ketiga yang terakhir tidak membaca PHIT sama sekali, dan itu bukan detail
 * dalam: ia yang membuat tahap dua dan tahap tiga boleh berbeda kurva, dan
 * yang membuat mesin harus menjepit hasilnya. Sebuah pilihan yang diam-diam
 * melepaskan tahap sebelumnya adalah pilihan yang tidak bisa dibaca dari
 * namanya.
 */
export const PHIE_NOTES: Record<string, string> = {
  total_shale:
    "The only one that reads the result of stage two. Shale porosity is " +
    "subtracted after PHIT is finished, so whatever total-porosity method was " +
    "used above, the result stays below that PHIT.",
  shale_corrected:
    "Its shale term sits inside the tool equation rather than being subtracted " +
    "afterwards, so this stage reads no PHIT at all — it may read a different " +
    "curve from stage two. Anything above PHIT is clamped to PHIT, and every " +
    "clamp is named in the run warnings.",
};

/** Sonik: titik serpihnya lewat lengkung yang sama dengan lognya. */
const SONIC_NOTE =
  "Its transform comes from the total-porosity stage — DTsh is computed with " +
  "the same equation as DT, because a shale term from another transform is no " +
  "correction but a mixture of two equations.";

/**
 * Metode beserta angka yang dibacanya, tanpa menyimpan apa pun sendiri.
 *
 * Kotak yang muncul mengikuti metode yang dipilih, dengan alasan yang sama
 * seperti pada kedua alat sebelumnya: PHIsh di sebelah porositas efektif
 * densitas adalah angka yang tidak menentukan apa pun pada hasilnya.
 */
export function PhieFields({
  params,
  available,
  onChange,
  disabled = false,
}: {
  params: ParameterValues;
  /** Mnemonik yang ada di set kurva aktif sumur ini. Tanpa itu tidak ada
   *  pemilih sumber sama sekali — rel ruang kerja memanggil komponen ini
   *  di tempat yang belum memuat daftar kurvanya. */
  available?: AvailableCurve[];
  onChange: (
    key: keyof ParameterValues,
    value: number | string | boolean | Record<string, string>,
  ) => void;
  disabled?: boolean;
}) {
  const method = String(params.phie_method ?? "total_shale");
  const sonic = String(params.phit_sonic_method ?? "wyllie");
  const reads = {
    density: method === "density_shale" || method === "density_neutron_shale",
    neutron: method === "neutron_shale" || method === "density_neutron_shale",
    sonic: method === "sonic_shale",
  };
  return (
    <>
      <ParamCaption>Method</ParamCaption>
      <Dropdown
        value={method}
        options={PHIE_METHODS}
        onChange={(value) => onChange("phie_method", value)}
        width="100%"
        disabled={disabled}
      />
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-4)" }}>
        {phieFormula(params)}
      </span>

      {method === "total_shale" && (
        <NumberField
          label="PHIsh"
          unit="v/v"
          value={Number(params.phi_shale)}
          step={0.01}
          min={0}
          max={1}
          onChange={(v) => onChange("phi_shale", v)}
          disabled={disabled}
        />
      )}

      {reads.density && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <NumberField
              label="RHOma"
              unit="g/cc"
              value={Number(params.rho_matrix)}
              step={0.01}
              onChange={(v) => onChange("rho_matrix", v)}
              disabled={disabled}
            />
            <NumberField
              label="RHOfl"
              unit="g/cc"
              value={Number(params.rho_fluid)}
              step={0.01}
              onChange={(v) => onChange("rho_fluid", v)}
              disabled={disabled}
            />
          </div>
          <NumberField
            label="RHOsh"
            unit="g/cc"
            value={Number(params.rho_shale)}
            step={0.01}
            onChange={(v) => onChange("rho_shale", v)}
            disabled={disabled}
          />
        </>
      )}

      {reads.neutron && (
        <NumberField
          label="NPHIsh"
          unit="v/v"
          value={Number(params.nphi_shale)}
          step={0.01}
          min={0}
          max={1}
          onChange={(v) => onChange("nphi_shale", v)}
          disabled={disabled}
        />
      )}

      {reads.sonic && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <NumberField
              label="DTma"
              unit="us/ft"
              value={Number(params.dt_matrix)}
              step={0.5}
              onChange={(v) => onChange("dt_matrix", v)}
              disabled={disabled}
            />
            {/* DTfl hanya dibaca Wyllie — Raymer-Hunt tidak punya suku fluida
                sama sekali, dan angka yang tidak mengubah hasil lebih buruk
                daripada tidak ada angka. */}
            {sonic === "wyllie" && (
              <NumberField
                label="DTfl"
                unit="us/ft"
                value={Number(params.dt_fluid)}
                step={1}
                onChange={(v) => onChange("dt_fluid", v)}
                disabled={disabled}
              />
            )}
          </div>
          <NumberField
            label="DTsh"
            unit="us/ft"
            value={Number(params.dt_shale)}
            step={1}
            onChange={(v) => onChange("dt_shale", v)}
            disabled={disabled}
          />
        </>
      )}

      {method === "density_neutron_shale" && (
        <Toggle
          label="Gas crossover correction (RMS)"
          checked={params.phit_gas_corrected !== false}
          onChange={(checked) => onChange("phit_gas_corrected", checked)}
          disabled={disabled}
        />
      )}

      <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
        {method === "total_shale" ? PHIE_NOTES.total_shale : PHIE_NOTES.shale_corrected}
      </span>
      {reads.sonic && (
        <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
          {SONIC_NOTE}
        </span>
      )}
      {available && (
        <CurveSourceFields
          params={params}
          available={available}
          channels={channelsRead(3, params)}
          onChange={(sources) => onChange("curve_sources", sources)}
          disabled={disabled}
        />
      )}

    </>
  );
}

/**
 * Kunci yang boleh berangkat dari jendela ini; sisanya bukan urusan PHIE.
 *
 * `phit_gas_corrected` ada di sini dan di `PHIT_KEYS` sekaligus, dan itu memang
 * satu kunci yang sama: apakah interval ini mengandung gas adalah satu
 * kenyataan tentang sumurnya, bukan dua setelan yang boleh berselisih antara
 * tahap dua dan tahap tiga. `phit_sonic_method` dibaca tahap ini tanpa boleh
 * diubah dari sini, dengan alasan yang sama dari arah sebaliknya — lengkung
 * soniknya dipilih di tahap dua, tempat ia pertama kali menentukan sesuatu.
 */
const PHIE_KEYS: (keyof ParameterValues)[] = [
  "phie_method", "phi_shale", "rho_matrix", "rho_fluid", "rho_shale",
  "nphi_shale", "dt_matrix", "dt_fluid", "dt_shale", "phit_gas_corrected",
  "curve_sources",
];

/**
 * Tahap porositas efektif sebagai jendelanya sendiri.
 *
 * Aturannya sama dengan kedua jendela sebelumnya: yang dikirim pulang hanyalah
 * yang benar-benar diubah, sebab tiap run dengan override menerbitkan versi
 * parameter set baru dan riwayat yang menyebut angka yang tidak berubah adalah
 * riwayat yang lebih sulit dibaca daripada tidak ada riwayat.
 */
export function PhieDialog({
  params,
  available,
  busy = false,
  disabled = false,
  onClose,
  onRun,
}: {
  params: ParameterValues;
  available?: AvailableCurve[];
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onRun: (overrides: Partial<ParameterValues>) => void;
}) {
  const [draft, setDraft] = useState<Partial<ParameterValues>>({});
  const merged = { ...params, ...draft } as ParameterValues;
  const changed = Object.fromEntries(
    PHIE_KEYS.filter((key) => key in draft && !sameParam(draft[key], params[key])).map(
      (key) => [key, draft[key]],
    ),
  ) as Partial<ParameterValues>;

  return (
    <Modal
      title="3 · Effective porosity"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={disabled}
            onClick={() => onRun(changed)}
          >
            Compute effective porosity
          </Button>
        </>
      }
    >
      <PhieFields
        params={merged}
        available={available}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        disabled={disabled || busy}
      />
      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {"The whole deterministic workflow is run again. Saturation is read " +
          "from this effective porosity, permeability from both, and the PHIE " +
          "cutoff decides net reservoir on the same curve."}
      </span>
    </Modal>
  );
}
